import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage, crmErrorDetail } from "../api/crmErrors";
import { fmtCount } from "../format";
import useSavedViews from "../hooks/useSavedViews";
import CrmSelect from "../ui/CrmSelect";
import CrmInput from "../ui/CrmInput";
import CrmTextarea from "../ui/CrmTextarea";
import CrmButton from "../ui/CrmButton";
import BroadcastConfirmDialog from "./BroadcastConfirmDialog";
import MergeFieldPicker, { insertAtCaret } from "../ui/MergeFieldPicker";
import { LABEL, TILE } from "../ui/classes";
import useEmailPreview from "./preview/useEmailPreview";
import EmailPreviewFrame from "./preview/EmailPreviewFrame";
import { CRM } from "../../../constants/testIds/crm";

const CURRENT_FILTER = "__current__";

/**
 * "Send broadcast" (spec Screen 6, worked example "64 → 61"): audience, subject, body,
 * Preview (renders the email as a client receives it, with a sample client, and states
 * the sendable count), Send — gated on
 * a FRESH preview (409 `AUDIENCE_CHANGED` is the server's half of this; editing anything
 * after a preview invalidates it client-side, forcing a re-preview before Send).
 *
 * `audienceFilters` is the Clients screen's CURRENT filter object, offered as "Current
 * filter" alongside the saved views — omitted (no such option) when the caller has none.
 */
export default function BroadcastComposer({ onSent, audienceFilters }) {
  const t = useT("crm");
  const tc = useT("common");
  const { views } = useSavedViews();
  const [viewId, setViewId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const area = useRef(null);
  // "Send test to me" needs no audience: it mails this draft to the practitioner alone.
  // The same audience-free draft also backs Preview while no audience is chosen: it shows
  // the email, but only the audience-bound broadcast preview below can unlock Send.
  const testDraft = useEmailPreview({ kind: "broadcast", subject: subject.trim(), body_html: body });

  const audience = () => (
    viewId === CURRENT_FILTER ? { filters: audienceFilters || {} } : (viewId ? { view_id: viewId } : null)
  );

  const insert = (token) => {
    const { value, caret } = insertAtCaret(area.current, body, token);
    setBody(value);
    requestAnimationFrame(() => { area.current?.focus(); area.current?.setSelectionRange(caret, caret); });
  };

  const runPreview = async () => {
    const aud = audience();
    if (!aud || !subject.trim() || !body.trim()) return;
    setBusy(true);
    try {
      const res = await crmApi.previewBroadcast({ audience: aud, subject: subject.trim(), body_html: body });
      // The preview freezes the exact inputs it was computed from: Send re-sends THESE,
      // never whatever the fields hold at click time (they may have drifted since).
      setPreview({ ...res, audience: aud, subject: subject.trim(), body_html: body });
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  // Any edit after a preview makes it stale: Send stays disabled until a fresh Preview.
  const invalidate = (setter) => (value) => { setter(value); setPreview(null); };

  const send = async () => {
    setBusy(true);
    try {
      await crmApi.sendBroadcast({
        audience: preview.audience, subject: preview.subject, body_html: preview.body_html,
        confirm_count: preview.sendable,
      });
      setConfirming(false);
      setPreview(null);
      toast.success(t("broadcast.sent", { n: fmtCount(preview.sendable) }));
      onSent?.();
    } catch (e) {
      const detail = crmErrorDetail(e);
      if (Number.isFinite(detail.sendable)) setPreview(null);
      toast.error(crmErrorMessage(e, t, tc));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const selectedView = views.find((v) => v.id === viewId);

  return (
    <div className="space-y-4" data-testid={CRM.broadcast}>
      <div>
        <label htmlFor="crm-broadcast-audience" className={LABEL}>{t("broadcast.audienceLabel")}</label>
        <CrmSelect id="crm-broadcast-audience" value={viewId} onChange={(e) => invalidate(setViewId)(e.target.value)}
          placeholder={t("broadcast.audienceLabel")} data-testid={CRM.broadcastAudience}
          options={[
            ...(audienceFilters ? [{ value: CURRENT_FILTER, label: t("broadcast.audienceCurrentFilter") }] : []),
            ...views.map((v) => ({ value: v.id, label: v.name })),
          ]} />
      </div>
      <div>
        <label htmlFor="crm-broadcast-subject" className={LABEL}>{t("broadcast.subjectLabel")}</label>
        <CrmInput id="crm-broadcast-subject" value={subject} onChange={(e) => invalidate(setSubject)(e.target.value)} data-testid={CRM.broadcastSubject} />
      </div>
      <div>
        <label htmlFor="crm-broadcast-body" className={LABEL}>{t("broadcast.bodyLabel")}</label>
        <CrmTextarea id="crm-broadcast-body" ref={area} rows={6} value={body} onChange={(e) => invalidate(setBody)(e.target.value)} data-testid={CRM.broadcastBody} />
      </div>
      <MergeFieldPicker onInsert={insert} />

      <div className="flex items-center gap-3">
        <CrmButton variant="outline" disabled={busy || testDraft.loading || !subject.trim() || !body.trim()}
          onClick={audience() ? runPreview : testDraft.load} data-testid={CRM.broadcastPreview}>
          {t("broadcast.preview")}
        </CrmButton>
        <CrmButton variant="outline" disabled={testDraft.sending || !subject.trim() || !body.trim()} onClick={testDraft.sendTest} data-testid={CRM.broadcastSendTest}>
          {t("emailPreview.sendTestToMe")}
        </CrmButton>
        <CrmButton disabled={busy || !preview} onClick={() => setConfirming(true)} data-testid={CRM.broadcastSend}>
          {t("broadcast.send")}
        </CrmButton>
      </div>

      {preview ? (
        <div className={`${TILE} p-4 text-sm text-eh-ink`} data-testid={CRM.broadcastPreviewPane}>
          <EmailPreviewFrame email={preview.rendered} />
          <p className="mt-3">{t("broadcast.previewStats", { sendable: fmtCount(preview.sendable), matched: fmtCount(preview.matched) })}</p>
        </div>
      ) : testDraft.email && !audience() && (
        <div className={`${TILE} p-4 text-sm text-eh-ink`} data-testid={CRM.broadcastPreviewPane}>
          <EmailPreviewFrame email={testDraft.email} />
          <p className="mt-3">{t("broadcast.previewNoAudience")}</p>
        </div>
      )}

      <BroadcastConfirmDialog open={confirming} onOpenChange={setConfirming} viewName={selectedView?.name} preview={preview} busy={busy} onConfirm={send} />
    </div>
  );
}
